import axios from "axios";
import VoiceToTextSDK from "./core/VoiceToTextSDK";
import { APP_KEY } from "../audio/utils/constant";
import { COSYVOICE_VOICE_XU, COSYVOICE_VOICE_XU_SPEECH_RATE } from "./constants/AliyunConstants";

/**
 * 消息级 TTS 控制器
 *
 * 单例模式：整个页面同时只允许一条消息在播报，
 * 点击新消息会自动停止旧消息并从头播放新消息。
 */
class MessageTTS {
  constructor() {
    this.sdk = null;
    this.tokenPromise = null;
    this.sdkInitPromise = null;
    this.currentMessageId = null;
    this.callbacks = {};
    this._listeners = new Set();
    this._streamingListeners = new Set();
    this.voice = null;
    // 全局自动播放开关：由 StreamingTTSController 在 setEnabled 时同步
    this.autoPlayEnabled = false;
    // 流式 TTS 控制器引用，切换点击消息时用来停掉流式播报，避免双音
    this._streamingTTS = null;
    // 当前流式播报中的消息 ID，用于高亮喇叭图标
    this.streamingMessageId = null;
  }

  /**
   * 注册流式 TTS 控制器引用，切换点击消息时用于停掉流式播报
   */
  registerStreamingTTS(instance) {
    this._streamingTTS = instance;
  }

  unregisterStreamingTTS() {
    this._streamingTTS = null;
  }

  /**
   * 按住说话录音时调用：立即停止当前流式播报，跳过当前回答剩余文本，
   * 并关闭当前消息的喇叭高亮状态。顶部自动播报总开关保持原样，
   * 发送新消息后恢复自动播报。
   */
  skipCurrentStreamingResponse() {
    this._streamingTTS?.skipCurrentResponse();
    this.clearStreamingMessageId();
  }

  /** 设置当前流式播报的消息 ID（去重：相同 ID 不重复通知，避免流式增量时频繁触发渲染） */
  setStreamingMessageId(msgId) {
    if (this.streamingMessageId === msgId) return;
    this.streamingMessageId = msgId;
    this._notifyStreamingListeners();
  }

  /** 清除流式播报消息 ID */
  clearStreamingMessageId() {
    this.streamingMessageId = null;
    this._notifyStreamingListeners();
  }

  /** 注册流式播报状态监听器，返回取消注册函数 */
  addStreamingListener(fn) {
    this._streamingListeners.add(fn);
    return () => this._streamingListeners.delete(fn);
  }

  /** 通知所有流式播报监听器 */
  _notifyStreamingListeners() {
    const id = this.streamingMessageId;
    this._streamingListeners.forEach((fn) => fn(id));
  }

  /**
   * 设置音色，切换后下次 speak 会重新初始化 SDK
   */
  setVoice(voice) {
    if (this.voice === voice) return;
    this.voice = voice;
    // 销毁旧 SDK，下次播报会用新音色重新创建
    this.destroy();
  }

  /** 注册监听器，返回取消注册函数 */
  addListener(fn) {
    this._listeners.add(fn);
    return () => this._listeners.delete(fn);
  }

  /** 通知所有监听器当前正在播报的消息 ID */
  _notifyListeners() {
    const id = this.currentMessageId;
    this._listeners.forEach((fn) => fn(id));
  }

  /**
   * 获取阿里云语音服务 token（仅首次调用时请求，后续复用）
   */
  getToken() {
    if (!this.tokenPromise) {
      this.tokenPromise = axios
        .get(`${window.PUBLIC_URL}/medagent/voice/getToken`)
        .then((res) => {
          if (res.data?.code === 200 && res.data?.data?.token) {
            return res.data.data.token;
          }
          throw new Error(res.data?.msg || "获取语音服务 token 失败");
        })
        .catch((err) => {
          this.tokenPromise = null;
          throw err;
        });
    }
    return this.tokenPromise;
  }

  /**
   * 初始化 SDK
   */
  async initSDK() {
    if (this.sdk) return this.sdk;

    if (!this.sdkInitPromise) {
      this.sdkInitPromise = (async () => {
        const token = await this.getToken();
        this.sdk = new VoiceToTextSDK({
          provider: "aliyun",
          appKey: APP_KEY,
          token,
          ...(this.voice ? { voice: this.voice } : {}),
          // 许鹏飞医生音色使用专属语速，其余音色保持默认
          ...(this.voice === COSYVOICE_VOICE_XU && { speechRate: COSYVOICE_VOICE_XU_SPEECH_RATE }),
        });

        this.sdk.onPlaybackComplete(() => {
          this.onComplete();
        });

        return this.sdk;
      })().catch((err) => {
        this.sdkInitPromise = null;
        throw err;
      });
    }

    return this.sdkInitPromise;
  }

  /**
   * 从消息对象中提取可朗读文本
   */
  extractText(message) {
    const textFields = [
      "text",
      "answer_know",
      "answer_info",
      "clarify_answer",
      "refuse_answer",
      "refuse_answer1",
      "refuse_answer2",
      "refuse_irrelevant",
      "faq_answer",
    ];

    const text = textFields
      .map((key) => message?.[key])
      .filter(Boolean)
      .map((v) => String(v).trim())
      .join("\n")
      .trim();

    if (!text) return "";

    return (
      text
        // 移除 markdown 图片
        .replace(/!\[.*?\]\(.*?\)/g, "")
        // 链接保留文案
        .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
        // 先把 "<" 数学比较符转义为可读文字：
        // "<" 后跟空白/数字/中文时绝不可能是 HTML 标签开头，可安全转义，
        // 避免被下面的标签清理从 "<" 一路误删到下一个 ">"（如 "< P3" 后的正文被整段删掉）
        .replace(/<(?=\s|\d|[\u4e00-\u9fa5])/g, "小于")
        // 百分位无空格写法（"<P3" / ">P3"）同样先转义，P3 不是 HTML 标签名
        .replace(/<P(\d+)/gi, "小于P$1")
        .replace(/>P(\d+)/gi, "大于P$1")
        // 只移除真正的 HTML/XML 标签
        .replace(/<\/?[a-zA-Z][^>]*>/g, "")
        // 合并多余空白
        .replace(/\s+/g, " ")
        .trim()
    );
  }

  /**
   * 播放指定消息
   *
   * @param {string} messageId 消息唯一标识
   * @param {string} text 要朗读的纯文本
   * @param {Object} callbacks 回调
   * @param {Function} callbacks.onStart 开始播放
   * @param {Function} callbacks.onComplete 播放完成或被停止
   * @param {Function} callbacks.onError 播放出错
   */
  async speak(messageId, text, callbacks = {}) {
    if (!text) {
      callbacks.onError?.(new Error("消息内容为空，无法播报"));
      return;
    }

    // 再次点击同一条消息：停止播报
    if (this.currentMessageId === messageId) {
      this.stop();
      return;
    }

    // 停止之前的播报（包含流式 TTS，避免同时两个声音）
    this._streamingTTS?.stop();
    this.stop(true);

    try {
      const sdk = await this.initSDK();

      this.currentMessageId = messageId;
      this.callbacks = callbacks;

      // 先清空旧文本/队列
      sdk.textManager?.clear();
      sdk.stop();

      // 写入完整文本并结束
      sdk.appendText(text);
      sdk.finish();

      sdk.play();

      this._notifyListeners();
      callbacks.onStart?.();
    } catch (err) {
      console.error("[MessageTTS] speak error:", err);
      this.currentMessageId = null;
      this._notifyListeners();
      callbacks.onError?.(err);
    }
  }

  /**
   * 停止当前播报
   *
   * @param {boolean} silent 是否静默触发完成回调（切换消息时避免 UI 闪烁）
   */
  stop(silent = false) {
    try {
      this.sdk?.stop();
    } catch (err) {
      console.error("[MessageTTS] stop error:", err);
    }

    const prevId = this.currentMessageId;
    this.currentMessageId = null;
    this._notifyListeners();

    if (!silent && prevId) {
      this.callbacks.onComplete?.();
    }
  }

  /**
   * 播放完成回调
   */
  onComplete() {
    const id = this.currentMessageId;
    this.currentMessageId = null;
    this._notifyListeners();
    if (id) {
      this.callbacks.onComplete?.();
    }
  }

  /**
   * 销毁 SDK
   */
  destroy() {
    try {
      this.sdk?.close();
    } catch (err) {
      console.error("[MessageTTS] destroy error:", err);
    }
    this.sdk = null;
    this.sdkInitPromise = null;
    this.tokenPromise = null;
    this.currentMessageId = null;
    this.callbacks = {};
  }
}

const messageTTS = new MessageTTS();
export default messageTTS;
